import {
  docAccessListResponseSchema,
  docAccessResponseSchema,
  type DocAccess,
  type DocAccessResponse,
  type DocAccessUpdate,
} from '@awwyeah/shared';

/**
 * The `/api/sharing` client. Permissions are the one part of this app that is *not* Yjs:
 * a CRDT everyone can write is exactly the wrong shape for "who may read this", so the
 * audience lives in Postgres behind a REST endpoint and the doc content stays in Yjs
 * (ROADMAP §1.3 is about collaborative state, which an ACL is not).
 */

export class SharingError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: 'same-origin', ...init });
  } catch {
    throw new SharingError('network_error');
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error =
      body && typeof body === 'object' && 'error' in body ? String(body.error) : 'request_failed';
    throw new SharingError(error, response.status);
  }
  return body;
}

/** One doc's audience. A doc nobody has ever shared answers `family` (never 404s). */
export async function fetchDocAudience(docName: string): Promise<DocAccessResponse> {
  const parsed = docAccessResponseSchema.safeParse(
    await request(`/api/sharing/${encodeURIComponent(docName)}`),
  );
  if (!parsed.success) throw new SharingError('unexpected_response');
  return parsed.data;
}

export async function saveDocAudience(
  docName: string,
  update: DocAccessUpdate,
): Promise<DocAccessResponse> {
  const parsed = docAccessResponseSchema.safeParse(
    await request(`/api/sharing/${encodeURIComponent(docName)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(update),
    }),
  );
  if (!parsed.success) throw new SharingError('unexpected_response');
  return parsed.data;
}

/** Every row that concerns me — one request the index screens use to badge their rows. */
export async function fetchMyDocAccess(): Promise<DocAccess[]> {
  const parsed = docAccessListResponseSchema.safeParse(await request('/api/sharing'));
  if (!parsed.success) throw new SharingError('unexpected_response');
  return parsed.data.entries;
}
